import type { ExternalTableSchema } from '@packages/table-builder';
import { IMPORT_EXPORT_SEMANTIC_IDS } from '@packages/table-builder/utils/importExportSemanticIds';

/**
 * Complete DynamicTableV2 schema for the Participant List page.
 *
 * Source of truth: PARTICIPANTS_TABLE_LIST (backend dynamic schema, v13).
 * Action IDs follow VERB_DOMAIN convention — last word = domain name.
 * Handlers are self-registering Action objects in domains/participant/participant.actions.ts,
 * auto-loaded by DynamicTableV2 via ActionRegistry.ensureActions().
 */
export const participantTable: ExternalTableSchema = {
  metadata: {
    title: 'Participants',
    entity: 'Participant',
    description:
      'Equity plan participant management — view, edit, invite, terminate, and export participants.',
  },

  dataSource: {
    endpoint: '/api/customer/api/app/participant/participants-list',
    method: 'GET',
    rowIdField: 'id',
    responseMapping: {
      items: 'items',
      totalCount: 'totalCount',
    },
    requestParams: {
      Filter: 'table.searchText',
      MaxResultCount: 'table.pageSize',
      SkipCount: 'table.pageOffset',
      Sorting: 'table.sorting',
    },

    filters: [
      {
        name: 'ApprovalStatusIds',
        semanticId: 'approvalStatusIds',
        placeholder: 'Approval Status',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/customer/api/app/participant/participant-statuses',
          labelField: 'statusName',
          valueField: 'id',
        },
      },
      {
        name: 'EmployeeType',
        semanticId: 'employeeTypeId',
        placeholder: 'Employee Type',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/customer/api/app/employee-type/employee-types-list',
          labelField: 'name',
          valueField: 'name',
        },
      },
      {
        name: 'EmployeeStatus',
        semanticId: 'employeeStatus',
        placeholder: 'Employee Status',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/customer/api/app/participant/participant-employee-statuses',
          labelField: 'name',
          valueField: 'name',
        },
      },
      {
        name: 'AccountStatus',
        semanticId: 'accountStatus',
        placeholder: 'Account Status',
        type: 'select',
        options: [
          { label: 'REGISTERED', value: 'REGISTERED' },
          { label: 'UNREGISTERED', value: 'UNREGISTERED' },
          { label: 'NOT REQUIRED', value: 'NOT REQUIRED' },
        ],
      },
      {
        name: 'IsEmailSend',
        semanticId: 'emailStatus',
        placeholder: 'Invitation Status',
        type: 'select',
        options: [
          { label: 'Sent', value: 'true' },
          { label: 'Not Sent', value: 'false' },
        ],
      },
      {
        name: 'BrokerAccountStatusIds',
        semanticId: 'brokerAccountStatusIds',
        placeholder: 'Broker Account Status',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/customer/api/app/participant-broker-account/broker-status-list',
          labelField: 'displayName',
          valueField: 'id',
        },
      },
    ],
  },

  columns: [
    {
      field: 'participantName',
      header: 'Participant Details',
      type: 'couple',
      sortable: true,
      width: 200,
      couple: {
        titleField: 'participantName',
        subtitleField: 'employeeId',
        showTag: true,
      },
    },
    {
      field: 'participantType',
      header: 'Type',
      type: 'badge',
      sortable: true,
      width: 120,
      align: 'center',
      badge: {
        statusMap: { Individual: 'info', Entity: 'success', 1: 'info', 2: 'success' },
        defaultVariant: 'default',
      },
      conditionalValues: { 1: 'Individual', 2: 'Entity' },
    },
    {
      field: 'employeeType',
      header: 'Employee Type',
      type: 'text',
      sortable: true,
      width: 200,
    },
    {
      field: 'employeeStatus',
      header: 'Employee Status',
      type: 'badge',
      sortable: true,
      width: 200,
      align: 'center',
      uppercase: true,
      badge: {
        statusMap: { Active: 'success', Terminated: 'error' },
        defaultVariant: 'default',
      },
    },
    {
      field: 'country',
      header: 'Country',
      type: 'text',
      sortable: true,
      width: 200,
    },
    {
      field: 'isEmailSent',
      header: 'Invitation Status',
      type: 'badge',
      sortable: true,
      width: 200,
      align: 'center',
      badge: {
        statusMap: { true: 'success', false: 'info' },
        defaultVariant: 'info',
      },
      conditionalValues: { true: 'Sent', false: 'Not Sent' },
    },
    {
      field: 'accountStatus',
      header: 'Account Status',
      type: 'badge',
      sortable: true,
      width: 200,
      align: 'center',
      badge: {
        statusMap: { REGISTERED: 'success', UNREGISTERED: 'error' },
        defaultVariant: 'default',
      },
    },
    {
      field: 'brokerInformation',
      header: 'Brokerage Information',
      type: 'badge',
      sortable: true,
      width: 200,
      align: 'center',
      badge: {
        statusMap: { COMPLETE: 'success', 'NOT COMPLETE': 'info', 'NOT REQUIRED': 'info' },
        defaultVariant: 'default',
      },
    },
    {
      field: 'taxCertification',
      header: 'Tax Certification',
      type: 'badge',
      sortable: true,
      width: 200,
      align: 'center',
      uppercase: true,
      badge: {
        statusMap: { UNCERTIFIED: 'error', CERTIFIED: 'success', 'NOT REQUIRED': 'info' },
        defaultVariant: 'default',
      },
    },
    {
      field: 'brokerAccountStatusId',
      header: 'Broker Account Status',
      type: 'badge',
      sortable: false,
      width: 200,
      align: 'center',
      badge: {
        statusMap: { 1: 'info', 2: 'info', 3: 'success', 4: 'success', 5: 'error', 6: 'info' },
        defaultVariant: 'default',
      },
      conditionalValues: {
        1: 'DRAFT',
        2: 'PENDING BROKER',
        3: 'BROKER RECEIVED',
        4: 'BROKER CREATED',
        5: 'BROKER REJECTED',
        6: 'NOT REQUIRED',
      },
    },
    {
      field: 'statusName',
      header: 'Approval Status',
      type: 'badge',
      sortable: true,
      width: 200,
      align: 'center',
      uppercase: true,
      badge: {
        statusMap: { Approved: 'success', Rejected: 'error', Pending: 'info' },
        defaultVariant: 'info',
      },
    },

    // ── Column-picker only ────────────────────────────────────────────

    {
      field: 'emailAddress',
      header: 'Email',
      type: 'text',
      sortable: true,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'dateOfBirth',
      header: 'Date of Birth',
      type: 'sensitiveReveal',
      sortable: true,
      width: 200,
      align: 'center',
      visibility: 'picker',
      sensitiveReveal: {
        maskType: 'DOB',
        maskFormat: 'full',
        idField: 'id',
        endpoint: '/api/customer/api/app/participant/date-of-birth-by-participant-id/{ParticipantId}',
        method: 'GET',
        responseField: '',
        dateFormat: 'MM/DD/YYYY',
        presenceField: 'hasDateOfBirth',
        placeholder: 'xx/xx/xxxx',
      },
    },
    {
      field: 'department',
      header: 'Department',
      type: 'text',
      sortable: true,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'jobTitle',
      header: 'Job Title',
      type: 'text',
      sortable: true,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'costCenter',
      header: 'Cost Center',
      type: 'text',
      sortable: true,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'hireDate',
      header: 'Hire Date',
      type: 'date',
      sortable: true,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'countryOfLegalResidency',
      header: 'Country Of Legal Residency',
      type: 'text',
      sortable: true,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'workCountry',
      header: 'Work Country',
      type: 'text',
      sortable: true,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'workCity',
      header: 'Work City',
      type: 'text',
      sortable: true,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'workState',
      header: 'Work State',
      type: 'text',
      sortable: true,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'socialSecurityNumber',
      header: 'SSN',
      type: 'sensitiveReveal',
      sortable: true,
      width: 200,
      visibility: 'picker',
      sensitiveReveal: {
        maskType: 'SSN',
        maskFormat: 'partial',
        idField: 'id',
        endpoint: '/api/customer/api/app/participant/social-security-number-by-participant-id/{ParticipantId}',
        method: 'GET',
        responseField: '',
      },
    },
    {
      field: 'residenceStatus',
      header: 'Residence Status',
      type: 'text',
      sortable: true,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'originalHireDate',
      header: 'Original Hire Date',
      type: 'date',
      sortable: true,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'settlementType',
      header: 'Settlement Type',
      type: 'text',
      sortable: true,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'isSection16',
      header: 'Section 16',
      type: 'badge',
      sortable: true,
      width: 200,
      align: 'center',
      visibility: 'picker',
      badge: { statusMap: { true: 'success', false: 'info' }, defaultVariant: 'info' },
      conditionalValues: { true: 'Yes', false: 'No' },
    },
    {
      field: 'isRule144',
      header: 'Rule 144',
      type: 'badge',
      sortable: true,
      width: 200,
      align: 'center',
      visibility: 'picker',
      badge: { statusMap: { true: 'success', false: 'info' }, defaultVariant: 'info' },
      conditionalValues: { true: 'Yes', false: 'No' },
    },
  ],

  actions: {
    row: [
      { id: 'SUMMARY_PARTICIPANT', label: 'Summary', semanticId: 'summaryAction' },
      { id: 'VIEW_PARTICIPANT', label: 'View', semanticId: 'viewAction' },
      { id: 'EDIT_PARTICIPANT', label: 'Edit', semanticId: 'editAction' },
      {
        id: 'SEND_INVITE_PARTICIPANT',
        label: 'Send Invite',
        semanticId: 'sendInviteAction',
        // Schema gate: hide for entity even if action classes have not loaded yet.
        visibilityCondition: {
          field: 'participantType',
          operator: 'NOT_IN',
          value: [2, '2', 'Entity', 'entity'],
        },
      },
      {
        id: 'RESEND_INVITE_PARTICIPANT',
        label: 'Resend Invite',
        semanticId: 'resendInviteAction',
        visibilityCondition: {
          field: 'participantType',
          operator: 'NOT_IN',
          value: [2, '2', 'Entity', 'entity'],
        },
      },
      { id: 'VIEW_PERSHING_PARTICIPANT', label: 'View Document', semanticId: 'pershingDocumentAction' },
      { id: 'VIEW_ISO_PARTICIPANT', label: 'View ISO', semanticId: 'viewISOAction' },
      { id: 'VIEW_REMARKS_PARTICIPANT', label: 'View Comments', semanticId: 'viewCommentsAction' },
      { id: 'EXPORT_PDF_PARTICIPANT', label: 'Export PDF', semanticId: 'exportPDFAction' },
      {
        id: 'TERMINATE_PARTICIPANT',
        label: 'Terminate',
        variant: 'danger',
        semanticId: 'terminateAction',
        visibilityCondition: {
          field: 'participantType',
          operator: 'NOT_IN',
          value: [2, '2', 'Entity', 'entity'],
        },
      },
      { id: 'VIEW_TERMINATION_PARTICIPANT', label: 'Termination Details', semanticId: 'terminationDetailsAction' },
      {
        id: 'PARTICIPANT_VIEW_READONLY_PARTICIPANT',
        label: 'Participant View (Readonly)',
        semanticId: 'participantViewReadOnlyAction',
      },
      {
        id: 'PARTICIPANT_VIEW_READWRITE_PARTICIPANT',
        label: 'Participant View (Read-write)',
        semanticId: 'participantViewReadWriteAction',
      },
    ],

    bulk: [
      { id: 'BULK_EXPORT_XLS_PARTICIPANT', label: 'Bulk Export XLS' },
      { id: 'BULK_SEND_INVITE_PARTICIPANT', label: 'Send Invite' },
      { id: 'BULK_EXPORT_PDF_PARTICIPANT', label: 'Bulk Export PDF' },
      { id: 'BULK_VIEW_PERSHING_PARTICIPANT', label: 'View Document' },
    ],

    toolbar: {
      primary: {
        id: 'ADD_PARTICIPANT',
        label: 'Add Participant',
        icon: 'add_circle_outline',
        actionType: 'dropdown',
        options: [
          { label: 'Add Participant', actionId: 'ADD_PARTICIPANT' },
          { label: 'Add Entity Participant', path: '/participants/form/create/entity' },
        ],
      },
      secondary: [
        {
          label: 'Download Template',
          icon: 'download',
          type: 'download' as const,
          semanticId: IMPORT_EXPORT_SEMANTIC_IDS.downloadTemplate,
          children: [
            { id: 'DOWNLOAD_TEMPLATE_PARTICIPANT', label: 'Download Individual Participant Template' },
            {
              id: 'DOWNLOAD_ENTITY_TEMPLATE_PARTICIPANT',
              label: 'Download Entity Participant Template',
            },
            { id: 'DOWNLOAD_YTD_TEMPLATE_PARTICIPANT', label: 'Download YTD Template' },
          ],
        },
        {
          label: 'Import Template',
          icon: 'upload',
          type: 'upload' as const,
          semanticId: IMPORT_EXPORT_SEMANTIC_IDS.importTemplate,
          children: [
            {
              id: 'IMPORT_PARTICIPANT',
              label: 'Import Individual Participant XLS',
              semanticId: IMPORT_EXPORT_SEMANTIC_IDS.importBulkParticipantXls,
            },
            {
              id: 'IMPORT_ENTITY_PARTICIPANT',
              label: 'Import Entity Participant XLS',
            },
            {
              id: 'IMPORT_YTD_PARTICIPANT',
              label: 'Import Bulk YTD XLS',
              semanticId: IMPORT_EXPORT_SEMANTIC_IDS.importBulkYtdXls,
            },
          ],
        },
      ],
    },
  },

  features: {
    search: { enabled: true },
    pagination: { mode: 'infiniteScroll', defaultSize: 50 },
    selection: { keyField: 'id', selectAllPages: true },
    columnPreferences: { persistenceKey: 'Participant', resizable: true, reorderable: true },
  },
};
